import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

import { InfoNotice } from '@shared/models/info-notice';

/**
 * How long each notice stays on screen, in milliseconds. The value is the card's.
 */
const AUTOPLAY_INTERVAL = 4600;

/**
 * Notices card for the dashboard, card
 * [86e33z2ub](https://app.clickup.com/t/86e33z2ub).
 *
 * Cycles through the notices it is given. It is custom to GetNet on purpose: the card
 * states it is not shared with the other partners, and the design system ships no
 * carousel to build on.
 *
 * ## It does not decide what to show
 *
 * The notices arrive as an input, already filtered. The card asks for content that is
 * "dynamic and parameterised", with each notice carrying its own condition, so the
 * condition belongs with the data and not here. This component only decides how to
 * present a list.
 *
 * ## One notice is not a carousel
 *
 * With a single notice there are no dots and no autoplay, which the card asks for
 * explicitly: a lone notice is a static block, and rotating it or drawing one dot next
 * to it would suggest there is something else to see.
 *
 * ## The skeleton is ours
 *
 * `cdsShimmer`, from `ui-directives`, would have been the obvious way to draw it. It puts
 * the right class and dimensions on the element, but the paint that goes with the class
 * lives inside another design system component's scoped styles, so it never reaches this
 * one. Copying that rule would not help either: it paints from
 * `hsla(var(--cds-foreground-600-hsl), …)`, and the GetNet theme leaves that channel
 * undefined, which makes the colour invalid and the block invisible. So the skeleton is
 * three blocks of our own, on tokens that exist.
 *
 * ## The autoplay lives in an effect
 *
 * The timer is created and destroyed by an `effect`, keyed on whether it should be
 * running at all — more than one notice, not paused, not loading. That is what makes
 * "pause on hover" a matter of flipping a signal rather than bookkeeping around a
 * handle, and it is what guarantees the timer dies with the component.
 *
 * Advancing writes the index from inside the interval, outside the effect's reactive
 * context, and the effect does not read the index. So the timer is not restarted every
 * time it fires.
 */
@Component({
  selector: 'info-card',
  standalone: true,
  templateUrl: './info-card.component.html',
  styleUrls: ['./info-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoCardComponent {
  /**
   * Notices to show, in the order they should appear.
   */
  public readonly notices = input.required<InfoNotice[]>();

  /**
   * Whether the notices are still on their way. Shows the skeleton while true.
   */
  public readonly loading = input(false);

  /**
   * Index of the notice on screen.
   */
  private readonly index = signal(0);

  /**
   * Whether the rotation is held. The pointer resting on the card sets it.
   */
  private readonly paused = signal(false);

  /**
   * Index of the notice on screen, for the dots to mark.
   */
  public readonly activeIndex = this.index.asReadonly();

  /**
   * Notice on screen, or `null` when there is none to show.
   */
  public readonly current = computed(() => this.notices()[this.index()] ?? null);

  /**
   * Whether there is more than one notice, which is what turns the card into a carousel.
   */
  public readonly isCarousel = computed(() => this.notices().length > 1);

  constructor() {
    effect((onCleanup) => {
      if (this.loading() || this.paused() || !this.isCarousel()) {
        return;
      }

      const timer = setInterval(() => this.advance(), AUTOPLAY_INTERVAL);

      onCleanup(() => clearInterval(timer));
    });

    /**
     * Brings the index back inside the list when the list shrinks.
     *
     * Without it a notice stopping being relevant while a later one is on screen would
     * leave the index past the end, and the card would go blank until the next tick.
     */
    effect(() => {
      const total = this.notices().length;

      if (total > 0 && this.index() >= total) {
        this.index.set(0);
      }
    });
  }

  /**
   * Holds the rotation.
   */
  public pause(): void {
    this.paused.set(true);
  }

  /**
   * Releases the rotation.
   */
  public resume(): void {
    this.paused.set(false);
  }

  /**
   * Shows the notice at the given position.
   *
   * @param index position of the notice in the list
   */
  public select(index: number): void {
    this.index.set(index);
  }

  /**
   * Moves to the next notice, wrapping around at the end.
   */
  private advance(): void {
    this.index.update((current) => (current + 1) % this.notices().length);
  }
}
